import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { LinkedInService } from 'src/app/services/social/linkedin.service';
import { GeneralService } from 'src/app/services/general.service';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
@Component({
  selector: 'app-linkedin-item',
  templateUrl: './linkedin-item.component.html',
  styleUrls: ['./linkedin-item.component.css']
})
export class LinkedinItemComponent implements OnInit {
  @Input() post: any;
  replyText: string = '';
  isReplying: boolean = false;
   @ViewChild('postModal') postModal!: TemplateRef<any>;
  showReplyInput: boolean = false;
  showPostModal: boolean = false;

  constructor(
    private linkedInService: LinkedInService,
    private toastr: ToastrService,
    private generalService: GeneralService,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit(): void {}

  getSafeUrl(url: string): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(url);
}

  openReplyModal() {
    this.showReplyInput = !this.showReplyInput;
    this.replyText = "";
  }

  canReply(): boolean {
    return this.getReplyMode() !== null;
  }

  cancelReply() {
    this.showReplyInput = false;
    this.replyText = "";
  }

submitReply() {
    if (!this.replyText.trim()) {
        this.toastr.error("Reply cannot be empty");
        return;
    }

    const replyMode = this.getReplyMode();
    if (!replyMode) {
        this.toastr.error("This LinkedIn item cannot be replied to.");
        return;
    }

    this.isReplying = true;

    const commentId = replyMode === 'comment'
        ? this.getCommentUrn()
        : this.getObjectUrn();

    this.linkedInService.replyToComment(commentId, this.replyText.trim()).subscribe(
        () => {
            this.isReplying = false;
            this.showReplyInput = false;
            this.post = { ...this.post, replied: true };
            this.replyText = "";
            this.toastr.success("Reply sent successfully.");
        },
        () => {
            this.isReplying = false;
            this.toastr.error("Could not send LinkedIn reply.");
        }
    );
}

  getAuthorName(): string {
    return this.post.actorName || 'LinkedIn User';
  }

  getActionLabel(): string {
    const post = this.post || {};
    const action = (post.action || post.item || '').toUpperCase();

    if (["COMMENT", "ADMIN_COMMENT", "COMMENT_EDIT", "COMMENT_DELETE"].includes(action)) {
      return 'commented on your LinkedIn content';
    }

    if (action === 'SHARE_MENTION' || post.itemType === 'mention') {
      return 'mentioned your LinkedIn page';
    }

    if (action === 'LIKE') {
      return 'reacted to your LinkedIn content';
    }

    return 'interacted with your LinkedIn content';
  }

  getReactionEmoji(): string {
    const map: Record<string, string> = {
        LIKE: '👍',
        PRAISE: '👏',
        EMPATHY: '❤️',
        INTEREST: '💡',
        APPRECIATION: '🙏',
        ENTERTAINMENT: '😄',
    };
    return map[(this.post.reactionType || 'LIKE').toUpperCase()] || '👍';
}

  openPostModal() {
    this.generalService.modal.open(this.postModal, {
      title: 'Post Details',
      ngbOptions: {
        backdrop: 'static',
        keyboard: false
      }
    });
  }

  closePostModal() {
    this.generalService.modal.closeActive();
  }

getPostText(): string {
  const post = this.post || {};
  return post.parentPost.commentary || '';
}

getPostMedia(): { type: 'image' | 'video', url: string, thumbnail?: string }[] {
    return this.post.parentPost.media 
        || this.post.media 
        || [];
}

getPostDate(): string {
  const post = this.post || {};
  const raw = (post.created && post.created.time) 
           || (post.lastModified && post.lastModified.time)
           || post.lastModifiedAt
           || post.publishedAt;
  if (!raw) return '';
  return new Date(raw).toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

  getContextText(): string {
    const post = this.post || {};
    const decoratedSourcePost = post.decoratedSourcePost || {};
    const linkedinObject = post.linkedinObject || {};
    return decoratedSourcePost.commentary || linkedinObject.commentary || '';
  }


  getReactionCount(): number {
    const post = this.post || {};
    const decoratedSourcePost = post.decoratedSourcePost || {};
    const counts = decoratedSourcePost.totalSocialActivityCounts || {};

    if (typeof post.reactionCount === 'number') return post.reactionCount;
    if (typeof post.reactions === "number") return post.reactions;
    if (post.reactions && post.reactions.count !== undefined) return post.reactions.count;
    return counts.numLikes || 0;
  }

  getCommentCount(): number {
    const post = this.post || {};
    const decoratedSourcePost = post.decoratedSourcePost || {};
    const counts = decoratedSourcePost.totalSocialActivityCounts || {};

    if (typeof post.commentCount === 'number') return post.commentCount;
    if (typeof post.comments === "number") return post.comments;
    if (post.comments && post.comments.count !== undefined) return post.comments.count;
    return counts.numComments || 0;
  }

  isMention(): boolean {
    const post = this.post || {};
    return (post.itemType || '').toLowerCase() === 'mention' ||
      (post.action || post.item || '').toUpperCase() === 'SHARE_MENTION';
  }

  private getReplyMode(): 'comment' | 'post' | null {
    if (this.getCommentUrn()) {
      return 'comment';
    }

    if (this.getObjectUrn()) {
      return 'post';
    }

    return null;
  }

  private getCommentUrn(): string {
    const post = this.post || {};
    const decoratedGeneratedActivity = post.decoratedGeneratedActivity || {};
    const generatedActivity = post.generatedActivity || {};

    return (
      (decoratedGeneratedActivity.comment && decoratedGeneratedActivity.comment.id) ||
      (generatedActivity.comment && generatedActivity.comment.id) ||
      post.commentUrn ||
      ''
    );
  }

  private getObjectUrn(): string {
    const post = this.post || {};
    const decoratedSourcePost = post.decoratedSourcePost || {};
    const linkedinObject = post.linkedinObject || {};

    return (
      post.sourcePost ||
      decoratedSourcePost.entity ||
      decoratedSourcePost.activity ||
      decoratedSourcePost.id ||
      post.postUrn ||
      linkedinObject.entity ||
      ''
    );
  }
}

